import { useEffect, useMemo, useRef } from 'react';
import type { GeoPoint } from '@/lib/geo/geoPoint';
import { shapesBoundingBox, type HoleShapes } from '@/lib/geo/holeGeometry';
import { planStaticImage, resolveImagerySource, type ImagerySource } from '@/lib/geo/imagery';
import {
  HOLE_TILE_ZOOM,
  TILE_SIZE,
  project,
  projectGeo,
  tilesCovering,
  unproject,
} from '@/lib/geo/slippyMap';
import styles from './SatelliteHole.module.css';

/**
 * The hole over real aerial imagery (canvas 1f/1g) — the signature screen, and the only
 * place in the app where a photograph is content rather than texture.
 *
 * North-up and unrotated: the imagery is Web Mercator, and rotating it would rotate the
 * photograph. The geometry is drawn over it **without a veil**, in terrain colours, as
 * outlines — the canvas is specific that here the GeoJSON is the content.
 *
 * Two imagery shapes are supported and they frame the hole differently:
 *
 * - **tiles** (Esri): pick a zoom, fetch the grid covering the padded box, and scale the
 *   whole thing to fill the stage. Resolution is unbounded.
 * - **static** (Google Static Maps): one image, so the zoom has to be chosen up front such
 *   that the hole fits inside the API's logical size ceiling. The image then scales up to
 *   the stage, which is why a very large stage looks softer here than with tiles.
 *
 * Both paths end in the same world-pixel transform, so the overlay code is shared.
 */

export interface SatelliteHoleProps {
  shapes: HoleShapes;
  teeName?: string | null;
  width: number;
  height: number;
  label: string;
  /** Optional measured point, drawn as a white dashed line from the tee. */
  target?: GeoPoint | null;
  /** Click-to-measure. Receives the geo point under the pointer. */
  onPickTarget?: (point: GeoPoint) => void;
  onImageFailed?: () => void;
  /** Reports which source was used, and why if it was not the configured one. */
  onSourceResolved?: (source: ImagerySource, fellBackBecause: string | null) => void;
}

export function SatelliteHole({
  shapes,
  teeName,
  width,
  height,
  label,
  target,
  onPickTarget,
  onImageFailed,
  onSourceResolved,
}: SatelliteHoleProps) {
  const failures = useRef(0);

  const { source, fellBackBecause } = useMemo(() => resolveImagerySource(), []);

  useEffect(() => {
    onSourceResolved?.(source, fellBackBecause);
    // Reporting once per source is the intent; a parent that re-creates the callback each
    // render must not turn this into a per-frame notification.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, fellBackBecause]);

  const model = useMemo(() => {
    const box = shapesBoundingBox(shapes);

    /**
     * Pad so the hole is never flush against the frame. Only the tile path pads here: tiles
     * scale continuously, so 12% asked is 12% got. The static path owns its own (smaller)
     * margin, because its zoom is quantised and generous padding costs a whole zoom step.
     */
    const padLng = (box.maxLongitude - box.minLongitude) * 0.12 || 1e-4;
    const padLat = (box.maxLatitude - box.minLatitude) * 0.12 || 1e-4;
    const padded = {
      minLongitude: box.minLongitude - padLng,
      maxLongitude: box.maxLongitude + padLng,
      minLatitude: box.minLatitude - padLat,
      maxLatitude: box.maxLatitude + padLat,
    };

    /**
     * Static: the zoom and the request size have to be chosen together, so `planStaticImage`
     * owns that and hands back the matching transform. Tiles: fixed high zoom, fit afterwards.
     */
    let zoom: number;
    let plan: ReturnType<typeof planStaticImage> | null = null;
    let displayScale: number;
    let originX: number;
    let originY: number;

    if (source.kind === 'static') {
      plan = planStaticImage(box, width, height, source.maxLogicalSize);
      zoom = plan.request.zoom;
      displayScale = plan.displayScale;
      originX = plan.originX;
      originY = plan.originY;
    } else {
      zoom = HOLE_TILE_ZOOM;
      const topLeft = project(padded.maxLatitude, padded.minLongitude, zoom);
      const bottomRight = project(padded.minLatitude, padded.maxLongitude, zoom);
      const worldW = Math.max(1, bottomRight.x - topLeft.x);
      const worldH = Math.max(1, bottomRight.y - topLeft.y);
      displayScale = Math.min(width / worldW, height / worldH);
      originX = (topLeft.x + bottomRight.x) / 2;
      originY = (topLeft.y + bottomRight.y) / 2;
    }

    const toScreen = (worldX: number, worldY: number) => ({
      x: width / 2 + (worldX - originX) * displayScale,
      y: height / 2 + (worldY - originY) * displayScale,
    });
    const projectPoint = (p: GeoPoint) => {
      const w = projectGeo(p, zoom);
      return toScreen(w.x, w.y);
    };
    const unprojectScreen = (sx: number, sy: number): GeoPoint =>
      unproject(
        (sx - width / 2) / displayScale + originX,
        (sy - height / 2) / displayScale + originY,
        zoom,
      );

    const tiles =
      source.kind === 'tiles'
        ? tilesCovering(
            padded.minLatitude,
            padded.maxLatitude,
            padded.minLongitude,
            padded.maxLongitude,
            zoom,
          ).map((t) => ({
            coord: t,
            key: `${t.z}/${t.x}/${t.y}`,
            ...toScreen(t.x * TILE_SIZE, t.y * TILE_SIZE),
          }))
        : [];

    const ring = (points: GeoPoint[]) =>
      points
        .map(projectPoint)
        .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(' ');

    const tee = shapes.tees.find((t) => t.name === teeName)?.point ?? shapes.tees[0]?.point ?? null;

    return {
      tiles,
      tileSize: TILE_SIZE * displayScale,
      staticRequest: plan?.request ?? null,
      staticStyle: plan?.imageBox ?? null,
      unprojectScreen,
      greenPath: shapes.green.length >= 3 ? ring(shapes.green) : null,
      fairwayPath: shapes.fairway.length >= 3 ? ring(shapes.fairway) : null,
      bunkerPaths: shapes.bunkers.map(ring),
      waterPaths: shapes.water.map(ring),
      obPaths: shapes.outOfBounds.map(ring),
      pinAt: projectPoint(shapes.pin),
      teeAt: tee ? projectPoint(tee) : null,
      targetAt: target ? projectPoint(target) : null,
    };
  }, [shapes, teeName, width, height, target, source]);

  /**
   * One failed image is fatal for static — there is only one, and a bad key or an over-quota
   * account fails it every time. For tiles a couple of edge misses is normal, so only a
   * majority means the source is actually down.
   */
  const noteFailure = () => {
    failures.current += 1;
    const threshold = source.kind === 'static' ? 1 : Math.max(2, Math.ceil(model.tiles.length / 2));
    if (failures.current >= threshold) onImageFailed?.();
  };

  return (
    <div className={styles.root} style={{ width, height }}>
      <div className={styles.tiles} aria-hidden="true">
        {source.kind === 'static' && model.staticRequest ? (
          <img
            src={source.staticUrl(model.staticRequest)}
            alt=""
            decoding="async"
            className={styles.staticImage}
            style={model.staticStyle ?? undefined}
            onError={noteFailure}
          />
        ) : (
          model.tiles.map((t) => (
            <img
              key={`${source.id}:${t.key}`}
              src={source.kind === 'tiles' ? source.tileUrl(t.coord) : ''}
              alt=""
              loading="eager"
              decoding="async"
              className={styles.tile}
              style={{
                left: t.x,
                top: t.y,
                width: model.tileSize,
                // +1px kills the hairline seams sub-pixel tile positions leave behind.
                height: model.tileSize + 1,
              }}
              onError={noteFailure}
            />
          ))
        )}
      </div>

      {/* Scrims. Top carries the pickers, bottom the mode toggle. */}
      <div className={styles.scrim} aria-hidden="true" />

      <svg
        className={styles.overlay}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
        onClick={
          onPickTarget
            ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                onPickTarget(model.unprojectScreen(e.clientX - rect.left, e.clientY - rect.top));
              }
            : undefined
        }
        style={onPickTarget ? { pointerEvents: 'auto', cursor: 'crosshair' } : undefined}
      >
        {model.fairwayPath && <polygon points={model.fairwayPath} className={styles.fairway} />}
        {model.obPaths.map((d, i) => (
          <polygon key={`ob${i}`} points={d} className={styles.ob} />
        ))}
        {model.waterPaths.map((d, i) => (
          <polygon key={`w${i}`} points={d} className={styles.water} />
        ))}
        {model.bunkerPaths.map((d, i) => (
          <polygon key={`b${i}`} points={d} className={styles.bunker} />
        ))}
        {model.greenPath && <polygon points={model.greenPath} className={styles.green} />}

        {model.teeAt && (
          <>
            <line
              x1={model.teeAt.x}
              y1={model.teeAt.y}
              x2={model.pinAt.x}
              y2={model.pinAt.y}
              className={styles.playLine}
            />
            <circle cx={model.teeAt.x} cy={model.teeAt.y} r={6} className={styles.teeDot} />
          </>
        )}

        {/* The ruler: white and thinner, so it never competes with the line of play. */}
        {model.teeAt && model.targetAt && (
          <>
            <line
              x1={model.teeAt.x}
              y1={model.teeAt.y}
              x2={model.targetAt.x}
              y2={model.targetAt.y}
              className={styles.rulerLine}
            />
            <circle
              cx={model.targetAt.x}
              cy={model.targetAt.y}
              r={13}
              className={styles.rulerRing}
            />
            <circle cx={model.targetAt.x} cy={model.targetAt.y} r={3} className={styles.rulerDot} />
          </>
        )}

        <line
          x1={model.pinAt.x}
          y1={model.pinAt.y}
          x2={model.pinAt.x}
          y2={model.pinAt.y - 30}
          className={styles.flagPole}
        />
        <polygon
          points={`${model.pinAt.x},${model.pinAt.y - 30} ${model.pinAt.x + 19},${model.pinAt.y - 23} ${model.pinAt.x},${model.pinAt.y - 16}`}
          className={styles.flag}
        />
        <circle cx={model.pinAt.x} cy={model.pinAt.y} r={5} className={styles.pinDot} />
      </svg>

      {/* Only when the source does not stamp its own — Google Static burns the logo in. */}
    </div>
  );
}
